import { useContext } from 'react';
import { canvasRef } from '../canvas/canvas';
import { ResetZoomIcon } from '../icons/reset-zoom';
import { ZoomInIcon } from '../icons/zoom-in';
import { ZoomOutIcon } from '../icons/zoom-out';
import { PreviewSizeContext } from '../preview-size';
import { calculateScale } from '../utils/calculate-canvas-transformation';
import { MAX_CANVAS_ZOOM, MIN_CANVAS_ZOOM } from '../utils/smooth-zoom';
import { useDimensions } from '../utils/use-context';
import { cn, Tooltip } from '@crabs-ai/ds-react';

export const CanvasZoomControls: React.FC = () => {
  const { size, setSize } = useContext(PreviewSizeContext);
  const { compositionWidth, compositionHeight } = useDimensions();

  const isFitMode = size.size === 'auto';
  const currentZoom = typeof size.size === 'number' ? size.size : 1;

  // button들의 비활성화 여부를 계산
  const isZoomInDisabled = !isFitMode && currentZoom >= MAX_CANVAS_ZOOM;
  const isZoomOutDisabled = !isFitMode && currentZoom <= MIN_CANVAS_ZOOM;

  const handleZoomIn = () => {
    if (isZoomInDisabled) return;

    setSize((oldSize) => {
      if (oldSize.size === 'auto') {
        const oldScale = calculateScale({
          canvasSize: canvasRef.current!.getBoundingClientRect(),
          compositionWidth: compositionWidth,
          compositionHeight: compositionHeight,
          previewSize: 'auto',
        });

        return {
          ...oldSize,
          size: oldScale * 1.2,
        };
      }
      return {
        ...oldSize,
        size: Math.min(oldSize.size * 1.2, MAX_CANVAS_ZOOM),
      };
    });
  };

  const handleZoomOut = () => {
    if (isZoomOutDisabled) return;

    setSize((oldSize) => {
      const oldScale = calculateScale({
        canvasSize: canvasRef.current!.getBoundingClientRect(),
        compositionWidth: compositionWidth,
        compositionHeight: compositionHeight,
        previewSize: 'auto',
      });
      if (oldSize.size === 'auto') {
        return {
          ...oldSize,
          size: oldScale * 0.8,
        };
      }
      return {
        ...oldSize,
        size: Math.max(oldSize.size * 0.8, MIN_CANVAS_ZOOM),
      };
    });
  };

  const handleFit = () => {
    setSize(() => ({
      translation: {
        x: 0,
        y: 0,
      },
      size: 'auto',
    }));
  };

  return (
    <div
      className={cn(
        'flex h-full items-center',
        'overflow-hidden rounded-3xs bg-sys-style-inverse-light inset-shadow-basic-sm',
      )}
    >
      {!isFitMode && (
        <Tooltip
          side="bottom"
          sideOffset={4}
          textNode="Reset to Auto"
          subTextNode="[0]"
          triggerProps={{ asChild: true }}
        >
          <button
            onClick={handleFit}
            data-is-fit-mode={isFitMode}
            className="flex h-full w-8 cursor-pointer items-center justify-center editor-starter-focus-ring text-sm hover:bg-white/5"
            aria-label="Reset to Auto"
            aria-keyshortcuts="0"
          >
            <ResetZoomIcon />
          </button>
        </Tooltip>
      )}
      <Tooltip
        side="bottom"
        sideOffset={4}
        textNode="Zoom out"
        subTextNode="[-]"
        triggerProps={{ asChild: true }}
      >
        <button
          onClick={handleZoomOut}
          disabled={isZoomOutDisabled}
          className={`flex h-full w-8 items-center justify-center editor-starter-focus-ring transition-colors ${
            isZoomOutDisabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:bg-white/5'
          }`}
          aria-label="Zoom out"
          aria-keyshortcuts="-"
        >
          <ZoomOutIcon
            data-disabled={isZoomOutDisabled}
            className="h-3 w-3 data-[disabled=true]:opacity-50 [&_path]:fill-sys-cont-neutral-default"
          />
        </button>
      </Tooltip>

      <div
        className={cn(
          'flex h-full min-w-[60px] cursor-default items-center justify-center',
          'text-center text-comp-moderate-small!',
        )}
      >
        <p className="text-sys-cont-neutral-default">
          {isFitMode ? 'Fit' : `${Math.round(currentZoom * 100)}%`}
        </p>
      </div>

      <Tooltip
        side="bottom"
        sideOffset={4}
        textNode="Zoom in"
        subTextNode="[+ / =]"
        triggerProps={{ asChild: true }}
      >
        <button
          onClick={handleZoomIn}
          disabled={isZoomInDisabled}
          className={`flex h-full w-8 items-center justify-center editor-starter-focus-ring ${
            isZoomInDisabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:bg-white/5'
          }`}
          aria-label="Zoom in"
          aria-keyshortcuts="+ ="
        >
          <ZoomInIcon
            data-disabled={isZoomInDisabled}
            className="h-3 w-3 data-[disabled=true]:opacity-50 [&_path]:fill-sys-cont-neutral-default"
          />
        </button>
      </Tooltip>
    </div>
  );
};
